// @flow

import React, { useState } from 'react';
import type { Node } from 'react';
import { useIntl } from 'react-intl';
import { useMutation } from '@apollo/client';
import { Heading, Link, Button } from '@riseart/common';
import { FormBuilder } from '@riseart/form';
import { meta as META_ENUM, email as EMAIL_ENUM } from 'Enum';
import { IsomorphicRipple } from 'shared_components/common/preloader/IsomorphicRipple';
import { UrlAssembler } from 'shared_services/riseart/utils/UrlAssembler';
import { ForgotPasswordFormModel } from 'shared_models/forms/ForgotPassword';
import CREATE_VALIDATION_MUTATION from 'shared_data/queries/auth/createValidation.graphql';

import { loginCls, loginFormCls, loginFormTitleCls } from 'shared_components/forms/login/Form.less';

/**
 * ForgotPasswordForm
 *
 * @param {Props} props
 */
export const ForgotPasswordForm = (): Node => {
  const intl = useIntl();
  const [createValidation, { loading }] = useMutation(CREATE_VALIDATION_MUTATION);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const signUpText = intl.formatMessage({ id: 'forms.auth_form.login_button_text' });
  const successButtonText = isSubmitted
    ? intl.formatMessage({
        id: 'forms.auth_form.forgotPassword.successButton',
      })
    : null;
  const handleSubmmit =
    (createValidationMutation: Function) =>
    ({ email }: Object) => {
      createValidationMutation({
        variables: { type: EMAIL_ENUM.validation.type.FORGOT_PASSWORD, email },
      })
        .then(() => setIsSubmitted(true))
        .catch(() => null); // Keep this to silent warning for unhandler promise rejection, as errors are handled by Appollo client
    };

  return (
    <div className={loginCls}>
      <IsomorphicRipple isActive={loading}>
        <Heading tag="h2" align="center" className={loginFormTitleCls}>
          {intl.formatMessage({ id: 'forms.auth_form.forgotPassword.title' })}
        </Heading>
        <Heading tag="h5" level="5" align="center" className={loginFormTitleCls}>
          {intl.formatMessage({
            id: `forms.auth_form.forgotPassword.${isSubmitted ? 'success' : 'subtitle'}`,
          })}
        </Heading>

        {!isSubmitted ? (
          <div>
            <FormBuilder
              intl={intl}
              className={loginFormCls}
              settings={ForgotPasswordFormModel.settings}
              fields={ForgotPasswordFormModel.fields}
              submitActionLabel={intl.formatMessage({
                id: 'forms.auth_form.forgotPassword.button',
              })}
              onSubmit={handleSubmmit(createValidation)}
            />
          </div>
        ) : (
          <Button
            isAnchor
            href={UrlAssembler.byRouteKey('home')}
            title={successButtonText}
            width="container"
          >
            {successButtonText}
          </Button>
        )}
        <div>
          <Link
            type="light"
            to={UrlAssembler.byRouteKey('login')}
            rel={META_ENUM.ATTRIBUTE_CONTENT.NO_FOLLOW}
            title={signUpText}
          >
            {signUpText}
          </Link>
        </div>
      </IsomorphicRipple>
    </div>
  );
};
